import React, {useEffect, useMemo, useState} from "react";
import {SegmentRequestContext} from "src/components/reports/lefReport/useSegmentCliExpansion";
import ServiceCollection from "src/services/ServiceCollection";
import {
    Alert,
    Box,
    Button,
    PropertyFilterProps,
    SpaceBetween
} from "@amzn/awsui-components-react";
import {
    Container, FormField,
    Grid,
    Header,
    Select,
    SelectProps
} from "@amzn/awsui-components-react/polaris";
import TPTaxLefReportFilterSelector from "src/components/reports/lefReport/TPTaxLefReportFilterSelector";
import {
    createGetTPReportFiltersRequestPayloadForLEFReport,
    FilterOption,
    FilterProperty,
    GetTPReportFiltersRequest
} from "src/models/reports/GetTPReportFilters";
import TPPropertyFilter from "src/components/shared/TPPropertyFilter";
import {GetFunctionalSegmentCategoriesByEntityRequest} from "src/models/reports/GetFunctionalSegmentCategoriesByEntity";
import {FetchReportRequest, PaginationConfig} from "src/models/reports/FetchReport";
import {DownloadTPTaxMultiCLIReportRequest} from "src/models/reports/DownloadTPTaxMultiCLIReport";
import {TPTaxReportMapResponse} from "src/models/reports/TPTaxReport";
import CONSTANTS, {IS_TESTED_PARTY_REPORT_ENABLED, LEF_DRILLDOWN_DIMENSION_ACCOUNT_ID} from "src/utils/constants";
import TPMultiCLIReportTableGrid from "src/components/reports/TPMultiCLIReportTableGrid";
import TPMultiCLIReportCollectionPreferences from "src/components/shared/TPMultiCLIReportCollectionPreferences";
import TPMultiCLIReportPagination from "src/components/shared/TPMultiCLIReportPagination";
import {useHistory, useParams} from "react-router-dom";
import {extractCompanyCodeFromCompanyCodeLabel, parseLefUIFilter} from "src/utils/ReportUtils";
import {handleFetchReportResponse} from "src/components/reports/TPMultiCLIReportsShared";
import { getFeatureFlags } from 'src/services/FeatureFlagsService';
import { useDashboardReportContext } from "src/components/reports/useDashboardReportContext";

// Value of the Functional Segment selector (and request param) meaning "every segment".
const ALL_FUNCTIONAL_SEGMENTS = "All";

export default function TPTaxLefReportView(props: { services: ServiceCollection }) {
    const DEFAULT_PAGE_SIZE: number = 15;
    const DEFAULT_FIRST_PAGE: number = 1;
    const {services} = props;
    const history = useHistory();
    const {filter} = useParams<{ filter: string }>();
    const [currentUrlParams, setCurrentUrlParams] = useState(parseLefUIFilter(decodeURIComponent(filter)) || '');
    const [selectedCompanyCodeOption, setSelectedCompanyCodeOption] = React.useState(null as SelectProps.Option | null);
    const [selectedPeriodOption, setSelectedPeriodOption] = React.useState(null as SelectProps.Option | null);
    const [selectedAccountingDayOption, setSelectedAccountingDayOption] = React.useState(null as SelectProps.Option | null);
    const [selectedFunctionalSegmentOption, setSelectedFunctionalSegmentOption] = React.useState({
        label: "All",
        value: "All"
    } as SelectProps.Option);
    const [functionalSegmentCategoriesByEntityRequestPayload, setFunctionalSegmentCategoriesByEntityRequestPayload] =
        React.useState(null as GetFunctionalSegmentCategoriesByEntityRequest | null);
    const [functionalSegmentCategoriesByEntity, functionalSegmentCategoriesByEntityLoading, functionalSegmentCategoriesByEntityError] =
        services.reportsService.getFunctionalSegmentCategoriesByEntity(functionalSegmentCategoriesByEntityRequestPayload);
    const [filtersDataRequestPayload, setFiltersDataRequestPayload] = React.useState(null as GetTPReportFiltersRequest | null);
    const [filtersData, filtersDataLoading, filtersDataError] = services.reportsService.getTPReportFilters(filtersDataRequestPayload);
    const [filterProperties, setFilterProperties] = React.useState([] as FilterProperty[]);
    const [filterOptions, setFilterOptions] = React.useState([] as FilterOption[]);
    const [downloadReportRequestPayload, setDownloadReportRequestPayload] = useState(null as unknown as DownloadTPTaxMultiCLIReportRequest);
    const [downloadReportResponse, downloadTPTaxReportLoading, downloadTPTaxReportError] = services.reportsService.downloadTPTaxMultiCLIReport(downloadReportRequestPayload);
    const [fetchReportRequest, setFetchReportRequest] = React.useState(null as FetchReportRequest | null);
    const [fetchReportResponse, fetchReportLoading, fetchReportError] = services.reportsService.fetchReportData(fetchReportRequest);
    const [reportData, setReportData] = React.useState(null as TPTaxReportMapResponse | null);

    const [searchFilterQuery, setSearchFilterQuery] = React.useState<PropertyFilterProps.Query>({
        tokens: [],
        operation: "or"
    });
    const [preferences, setPreferences] = React.useState({
        pageSize: DEFAULT_PAGE_SIZE
    });
    const [pageConfig, setPageConfig] = React.useState<PaginationConfig>({
        pageNumber: DEFAULT_FIRST_PAGE,
        pageSize: DEFAULT_PAGE_SIZE
    });
    const [lastPage, setLastPage] = React.useState(DEFAULT_FIRST_PAGE);
    const [showProviderCodeBanner, setShowProviderCodeBanner] = React.useState(false);
    const [isPassthroughDrilldownEnabled, setIsPassthroughDrilldownEnabled] = useState(false);
    const [lefSegmentDrillDownEnabled, setLefSegmentDrillDownEnabled] = useState(false);
    const [lefCliProvenanceEnabled, setLefCliProvenanceEnabled] = useState(false);

    // Drill-down selection. Lives here, not in the grid, so it survives Functional Segment changes and
    // paging (both of which re-fetch and re-render the grid) and resets only on a new entity/period/day.
    const [selectedDrillDownDimensionId, setSelectedDrillDownDimensionId] =
        useState<string>(LEF_DRILLDOWN_DIMENSION_ACCOUNT_ID);
    const [showZeroValueSegments, setShowZeroValueSegments] = useState(false);

    useEffect(() => {
        getFeatureFlags().then((flags) => {
            setIsPassthroughDrilldownEnabled(flags.isPassthroughDrilldownEnabled);
            setLefSegmentDrillDownEnabled(flags.lefSegmentDrillDownEnabled);
            setLefCliProvenanceEnabled(flags.lefCliProvenanceEnabled);
        }).catch((err) => {
            console.error('Failed to load feature flags', err);
        });
    }, []);

    // CLI provenance (Stories 3.1 + 3.2): lets the grid fetch one functional segment on demand and show
    // its CLIs next to that segment's total. Only offered for the "All" view — the one that has segment
    // totals and the reconciliation columns — and keyed off the request that produced the data on
    // screen, not the dropdown, so the two can never disagree mid-refetch.
    const segmentRequestContext = useMemo((): SegmentRequestContext | null => {
        if (!lefCliProvenanceEnabled || !fetchReportRequest
            || fetchReportRequest.params?.functionalSegmentCategory !== ALL_FUNCTIONAL_SEGMENTS) {
            return null;
        }
        const baseRequest = fetchReportRequest;
        return {
            // Pagination is deliberately excluded: paging the base report keeps expansions and caches.
            contextKey: JSON.stringify({
                reportName: baseRequest.reportName,
                params: baseRequest.params,
                filterConfig: baseRequest.filterConfig ?? null,
            }),
            buildRequest: (segmentCategory, pageNumber, pageSize) => ({
                ...baseRequest,
                params: { ...baseRequest.params, functionalSegmentCategory: segmentCategory },
                paginationConfig: { pageNumber, pageSize },
            }),
        };
    }, [lefCliProvenanceEnabled, fetchReportRequest]);

    // Event-driven redesign: period + accounting day come from the dashboard (URL query); the on-page
    // period/day dropdowns are hidden and only the entity selector remains. Seed period/day from context.
    const dashboardContext = useDashboardReportContext(props.services.messageService);
    const dashboardDriven = dashboardContext.eventDrivenTpTaxReportingEnabled;
    // Multi-CLI FRAMES currency display is gated behind the reportCurrencyOverlay flag, read from the
    // shared dashboard-report context so both LEF and CWB source it from one place.
    const showCliCurrency = dashboardContext.reportCurrencyOverlayEnabled;
    // Flag RESOLVED and off (not the default-false pre-resolution value). Legacy URL rewrites that drop
    // the dashboard's ?period&accountingDay query must key on this so a click during the brief unresolved
    // window on a dashboard-mode deep link doesn't strip the query and bounce back to the dashboard.
    const legacyMode = dashboardContext.flagResolved && !dashboardDriven;
    useEffect(() => {
        if (dashboardContext.periodOption && dashboardContext.accountingDayOption) {
            setSelectedPeriodOption(dashboardContext.periodOption);
            setSelectedAccountingDayOption(dashboardContext.accountingDayOption);
        }
    }, [dashboardContext.periodOption, dashboardContext.accountingDayOption]);

    useEffect(() => {
        if (!filtersDataError && !functionalSegmentCategoriesByEntityError && !downloadTPTaxReportError && !fetchReportError) {
            return;
        }
        props.services.messageService.showErrorAutoDismissBanner(
            filtersDataError || functionalSegmentCategoriesByEntityError || downloadTPTaxReportError || fetchReportError);
    }, [filtersDataError, functionalSegmentCategoriesByEntityError, downloadTPTaxReportError, fetchReportError]);

    useEffect(() => {
        if (selectedCompanyCodeOption && selectedPeriodOption) {
            setFunctionalSegmentCategoriesByEntityRequestPayload({
                entityCode: selectedCompanyCodeOption.value as string,
                period: selectedPeriodOption.value as string
            });
        }
    }, [selectedCompanyCodeOption, selectedPeriodOption]);

    useEffect(() => {
        if (filtersData) {
            setFilterProperties(filtersData.filterProperties);
            setFilterOptions(filtersData.filterOptions);
        }
    }, [filtersData]);

    useEffect(() => {
        handleFetchReportResponse(
            fetchReportResponse,
            setReportData,
            setLastPage,
            (message) => props.services.messageService.showErrorAutoDismissBanner(message)
        );
        if (fetchReportResponse) {
            // Report reference ID format history:
            // - Old format: TP_TAX_LEF#<providerCompanyCode>#<period>#<accountingDay>  (e.g. TP_TAX_LEF#B614#FEB-26#7)
            //   Reports generated in this format were filtered by providerCompanyCode, NOT testedPartyCompanyCode.
            //   A warning banner is shown to inform users that the data in this report is based on providerCompanyCode.
            // - New format: TP_TAX_LEF_V1#<testedPartyCode>#<period>#<accountingDay>  (e.g. TP_TAX_LEF_V1#B614#FEB-26#7)
            //   Reports generated in this format are correctly filtered by testedPartyCompanyCode.
            //   No banner is shown for this format.
            // The prefix check on "TP_TAX_LEF#" (without _V1) identifies old-format reports only,
            // since "TP_TAX_LEF_V1#" does NOT start with "TP_TAX_LEF#".
            // Banner only applies when the tested-party feature is enabled (legacy reports flagged for migration).
            setShowProviderCodeBanner(
                IS_TESTED_PARTY_REPORT_ENABLED
                    && (fetchReportResponse.reportReferenceId?.startsWith("TP_TAX_LEF#") ?? false)
            );
        }
    }, [fetchReportResponse]);

    useEffect(() => {
        if (downloadReportResponse && downloadReportResponse.reportURL) {
            window.open(downloadReportResponse.reportURL);
        }
    }, [downloadReportResponse])

    useEffect(() => {
        if (selectedAccountingDayOption && selectedPeriodOption && selectedCompanyCodeOption) {
            setFetchReportRequest({
                reportName: CONSTANTS.REPORT_TYPES.TP_TAX_LEF_REPORT,
                params: {
                    accountingDay: selectedAccountingDayOption?.value as string,
                    period: selectedPeriodOption?.value as string,
                    ...(IS_TESTED_PARTY_REPORT_ENABLED
                        ? { testedPartyCompanyCode: selectedCompanyCodeOption?.value as string }
                        : { providerCompanyCode: selectedCompanyCodeOption?.value as string }),
                    functionalSegmentCategory: selectedFunctionalSegmentOption?.value as string
                },
                paginationConfig: pageConfig,
                filterConfig: {
                    propertyFilters: [...searchFilterQuery.tokens],
                    propertyFiltersOperation: searchFilterQuery.operation
                }
            });
        }
    }, [pageConfig]);

    useEffect(() => {
        if (selectedAccountingDayOption && selectedPeriodOption && selectedCompanyCodeOption) {
            setPageConfig({
                pageNumber: DEFAULT_FIRST_PAGE, pageSize: preferences.pageSize
            });
        }
    }, [preferences, searchFilterQuery, selectedFunctionalSegmentOption]);

    useEffect(() => {
        if (currentUrlParams.companyCodeLabel
            && !(selectedAccountingDayOption && selectedPeriodOption && selectedCompanyCodeOption)
        ) {
            setSelectedAccountingDayOption({
                value: currentUrlParams.accountingDay,
                label: currentUrlParams.accountingDay
            });
            setSelectedPeriodOption({value: currentUrlParams.period, label: currentUrlParams.period});
            setSelectedCompanyCodeOption({
                value: extractCompanyCodeFromCompanyCodeLabel(currentUrlParams.companyCodeLabel),
                label: currentUrlParams.companyCodeLabel
            });
            setFiltersDataRequestPayload(
                createGetTPReportFiltersRequestPayloadForLEFReport(currentUrlParams.accountingDay as string,
                    currentUrlParams.period as string,
                    extractCompanyCodeFromCompanyCodeLabel(currentUrlParams.companyCodeLabel))
            );
        }

        if (selectedAccountingDayOption && selectedPeriodOption && selectedCompanyCodeOption) {
            setSelectedFunctionalSegmentOption({
                label: "All",
                value: "All"
            });

            setFiltersDataRequestPayload(
                createGetTPReportFiltersRequestPayloadForLEFReport(selectedAccountingDayOption.value as string,
                    selectedPeriodOption.value as string,
                    selectedCompanyCodeOption.value as string
                )
            )
        }

        setSearchFilterQuery({
            tokens: [],
            operation: "or"
        });

        // A new entity/period/day is a new report, so the drill-down starts from Account again. Note
        // this effect does NOT run on a Functional Segment or page change, which is exactly why the
        // selection persists across those.
        setSelectedDrillDownDimensionId(LEF_DRILLDOWN_DIMENSION_ACCOUNT_ID);
        setShowZeroValueSegments(false);
    }, [currentUrlParams]);

    const onDownloadClicked = () => {
        setDownloadReportRequestPayload({
            reportName: CONSTANTS.REPORT_TYPES.TP_TAX_LEF_REPORT,
            params: {
                period: selectedPeriodOption?.value as string,
                ...(IS_TESTED_PARTY_REPORT_ENABLED
                    ? { testedPartyCompanyCode: selectedCompanyCodeOption?.value as string }
                    : { providerCompanyCode: selectedCompanyCodeOption?.value as string }),
                accountingDay: selectedAccountingDayOption?.value as string,
                functionalSegmentCategory: selectedFunctionalSegmentOption?.value as string
            },
            filterConfig: (searchFilterQuery.tokens.length === 0) ? null : {
                propertyFilters: [...searchFilterQuery.tokens],
                propertyFiltersOperation: searchFilterQuery.operation
            }
        })
    }

    return (
        <SpaceBetween direction="vertical" size="xl">
            <Grid gridDefinition={[{colspan: 10}, {colspan: 2}]}>
                <TPTaxLefReportFilterSelector
                    services={services}
                    selectedCompanyCode={selectedCompanyCodeOption}
                    selectedPeriodOption={selectedPeriodOption}
                    selectedAccountingDayOption={selectedAccountingDayOption}
                    hidePeriodAndDay={dashboardDriven}
                    skipPeriods={dashboardDriven}
                    onCompanyCodeSelected={(companyCodeOption) => setSelectedCompanyCodeOption({
                        label: companyCodeOption?.label,
                        value: companyCodeOption?.value
                    })}
                    onPeriodSelected={(periodOption) => setSelectedPeriodOption({
                        label: periodOption?.label,
                        value: periodOption?.value
                    })}
                    onAccountingDaySelected={(accountingDayOption) => setSelectedAccountingDayOption({
                        label: accountingDayOption?.label,
                        value: accountingDayOption?.value
                    })}
                />
                <Box padding={{top: "xl"}}>
                    <SpaceBetween direction="horizontal" size="l">
                        <Button
                            variant="primary"
                            onClick={() => {
                                const newUrlParams = `${selectedCompanyCodeOption?.label}+${selectedPeriodOption?.value}+${selectedAccountingDayOption?.value}`;
                                setCurrentUrlParams(parseLefUIFilter(newUrlParams));
                                // Legacy pages carry the filter in the URL path; dashboard-driven pages
                                // keep period/day in the query string, so don't overwrite the route.
                                const encodedParams = encodeURIComponent(newUrlParams.replace(/\./g, '*'));
                                if (legacyMode) {
                                    history.push(`/reports/lef/${encodedParams}`);
                                } else if (dashboardDriven) {
                                    // Dashboard mode: reflect the viewed report in the path but keep the
                                    // ?period&accountingDay query (dropping it trips the redirect guard).
                                    history.push(`/reports/lef/${encodedParams}${history.location.search}`);
                                }
                            }}
                            disabled={!(!!selectedCompanyCodeOption && !!selectedPeriodOption && !!selectedAccountingDayOption)}
                            loading={fetchReportLoading}
                        >
                            View Report
                        </Button>
                    </SpaceBetween>
                </Box>
                <Box/>
            </Grid>
            {showProviderCodeBanner && (
                <Alert type="warning" dismissible onDismiss={() => setShowProviderCodeBanner(false)}>
                    Data in this report is based on providerCompanyCode instead of testedPartyCompanyCode.
                </Alert>
            )}
            <Container header={
                <>
                    <Header
                        variant={"h2"}
                        actions={
                            <Button
                                disabled={(reportData === null) || fetchReportLoading}
                                onClick={onDownloadClicked}
                                loading={downloadTPTaxReportLoading}>
                                Download Full Report
                            </Button>}
                    >
                        Report Details
                    </Header>

                    <Grid gridDefinition={[
                        {colspan: 4},
                        {colspan: 4},
                        {colspan: 4}
                    ]}>
                        <FormField label="&nbsp;">
                            <TPPropertyFilter
                                filterProperties={filterProperties}
                                filterOptions={filterOptions}
                                filtersDataLoading={filtersDataLoading}
                                query={searchFilterQuery}
                                setQuery={setSearchFilterQuery}
                            />
                        </FormField>
                        <FormField label="Functional Segment">
                            <Select
                                statusType={functionalSegmentCategoriesByEntityLoading ? "loading" : "finished"}
                                loadingText="Fetching Functional Segments"
                                selectedOption={selectedFunctionalSegmentOption}
                                onChange={({detail}) =>
                                    setSelectedFunctionalSegmentOption(detail.selectedOption)
                                }
                                options={
                                    functionalSegmentCategoriesByEntity == null
                                        ? []
                                        : functionalSegmentCategoriesByEntity.functionalSegmentCategoriesList.map(
                                            (functionalSegmentCategoryByEntity) => ({
                                                label: functionalSegmentCategoryByEntity,
                                                value: functionalSegmentCategoryByEntity,
                                            })
                                        )
                                }
                                placeholder="Select Option"
                                virtualScroll={true}
                                filteringType="auto"
                                disabled={(reportData === null) || fetchReportLoading}
                            />
                        </FormField>
                        <Box float={"right"} padding={{top: "xl"}}>
                            <SpaceBetween size={"xs"} direction={"horizontal"}>
                                <TPMultiCLIReportPagination
                                    currentPageIndex={pageConfig.pageNumber}
                                    pagesCount={lastPage}
                                    pageSize={pageConfig.pageSize}
                                    disabled={fetchReportResponse === null || fetchReportLoading}
                                    setPageConfig={setPageConfig}
                                />
                                <TPMultiCLIReportCollectionPreferences
                                    fetchReportResponse={fetchReportResponse}
                                    fetchReportLoading={fetchReportLoading}
                                    setPreferences={setPreferences}
                                    preferences={preferences}
                                    options={[5, 10, 15]}
                                />
                            </SpaceBetween>
                        </Box>
                    </Grid>
                </>
            }>
                {
                    reportData && <TPMultiCLIReportTableGrid
                        reportData={reportData}
                        services={services}
                        fetchReportLoading={fetchReportLoading}
                        isPassthroughDrilldownEnabled={isPassthroughDrilldownEnabled}
                        showCliCurrency={showCliCurrency}
                        lefSegmentDrillDownEnabled={lefSegmentDrillDownEnabled}
                        selectedDrillDownDimensionId={selectedDrillDownDimensionId}
                        onDrillDownDimensionChange={setSelectedDrillDownDimensionId}
                        showZeroValueSegments={showZeroValueSegments}
                        onShowZeroValueSegmentsChange={setShowZeroValueSegments}
                        segmentRequestContext={segmentRequestContext}
                        knownSegmentCategories={functionalSegmentCategoriesByEntity?.functionalSegmentCategoriesList}
                    />
                }
            </Container>
        </SpaceBetween>
    );
}
